import type {
  ApiResponse,
  CertificationWithDetails,
  BOKDomain,
  Question,
  ExamStartRequest,
  ExamStartResponse,
  SubmitAnswerRequest,
  SubmitAnswerResponse,
  FinalizeExamResponse,
  DailyChallengeResponse,
  BookmarkToggleResponse,
  UserBookmark,
  User,
  AuthResponse,
  GoogleAuthRequest,
  BillingStatusResponse,
  CreateCheckoutRequest,
  CreateCheckoutResponse,
  CreatePortalResponse
} from '@shared/types';

const BASE_URL = '/api/v1';
const TOKEN_KEY = 'biopharm_gxp_token';

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setStoredToken(token: string | null): void {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch {
    // ignore
  }
}

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const url = `${BASE_URL}${endpoint}`;
  const token = getStoredToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>)
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    ...options,
    headers
  });

  const payload: ApiResponse<T> = await res.json();
  if (!res.ok || !payload.success) {
    const errorMsg = payload.error?.message || `Request failed with status ${res.status}`;
    throw new Error(errorMsg);
  }

  return payload.data as T;
}

export const api = {
  // Authentication (Google Sign-In)
  googleAuth: (data: GoogleAuthRequest) =>
    request<AuthResponse>('/auth/google', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  getGoogleAuthUrl: (redirectUri?: string) => {
    const query = redirectUri ? `?redirect_uri=${encodeURIComponent(redirectUri)}` : '';
    return request<{ url: string; client_id: string }>(`/auth/google/url${query}`);
  },

  getMe: () => request<User>('/auth/me'),

  logout: () =>
    request<{ logged_out: boolean }>('/auth/logout', {
      method: 'POST'
    }),

  // Stripe Billing & 14-Day Free Trial
  getBillingStatus: () => request<BillingStatusResponse>('/billing/status'),

  createCheckoutSession: (data: CreateCheckoutRequest) =>
    request<CreateCheckoutResponse>('/billing/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  createPortalSession: () =>
    request<CreatePortalResponse>('/billing/create-portal-session', {
      method: 'POST'
    }),

  // Health
  getHealth: () => request<{ status: string; version: string; stats?: Record<string, number> }>('/health'),

  // Certifications
  getCertifications: () => request<CertificationWithDetails[]>('/certifications'),
  getDomains: (certId: string) => request<BOKDomain[]>(`/certifications/${certId}/domains`),

  // Questions
  getQuestions: (params?: { cert_id?: string; domain_id?: string; bloom_level?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.cert_id) query.set('cert_id', params.cert_id);
    if (params?.domain_id) query.set('domain_id', params.domain_id);
    if (params?.bloom_level) query.set('bloom_level', params.bloom_level);
    if (params?.limit) query.set('limit', String(params.limit));
    return request<Question[]>(`/questions?${query.toString()}`);
  },

  // Daily Challenge (KV Cached)
  getDailyChallenge: () => request<DailyChallengeResponse>('/questions/daily-challenge'),

  // Exam Engine
  startExam: (data: ExamStartRequest) =>
    request<ExamStartResponse>('/exams/start', {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  submitAnswer: (sessionId: string, data: SubmitAnswerRequest) =>
    request<SubmitAnswerResponse>(`/exams/${sessionId}/submit-answer`, {
      method: 'POST',
      body: JSON.stringify(data)
    }),

  finalizeExam: (sessionId: string) =>
    request<FinalizeExamResponse>(`/exams/${sessionId}/finalize`, {
      method: 'POST'
    }),

  // Bookmarks
  toggleBookmark: (questionId: string, notes?: string) =>
    request<BookmarkToggleResponse>('/bookmarks/toggle', {
      method: 'POST',
      body: JSON.stringify({ question_id: questionId, notes })
    }),

  getBookmarks: () => request<UserBookmark[]>('/bookmarks'),

  // Spaced Repetition
  getSpacedReview: (userId?: string) => {
    const query = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
    return request<{ total_due: number; questions: Question[] }>(`/exams/spaced-review${query}`);
  },

  // CRON Simulation
  triggerDailyCron: () =>
    request<{ sentCount: number; questionId?: string }>('/cron/trigger-daily', {
      method: 'POST'
    })
};
